import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFullBalance, doCustodyWithdraw, getClientFund, requestFundWithdraw } from '../../services/ExchangeService';
import { getSettings } from '../../services/SettingsService';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import WalletCard from './WalletCard';
import Toast from '../../components/Toast';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import SelectFiat, { setDefaultFiat, getDefaultFiat, formatFiatAmount } from '../../components/SelectFiat';
import './Wallet.css';

function apiError(err) {
    const raw = err?.response ? err.response.data : err?.message;
    if (typeof raw === 'string') return raw;
    if (raw && raw.msg) return raw.msg;
    if (raw && raw.message) return raw.message;
    return raw ? JSON.stringify(raw) : (err?.message || 'Erro inesperado');
}

function Wallet() {

    const isCustody = localStorage.getItem('isCustody') === 'true';
    const [balances, setBalances] = useState([]);
    const [fiat, setFiat] = useState(getDefaultFiat());
    const [totalFiat, setTotalFiat] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isFuture, setIsFuture] = useState(false);
    const [walletKind, setWalletKind] = useState('');
    const [allocation, setAllocation] = useState(null);
    const [pnl, setPnl] = useState(null);
    const [pnlPerc, setPnlPerc] = useState(null);
    const [pnlMode, setPnlMode] = useState('');
    const [pixKey, setPixKey] = useState('');
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);
    const [fund, setFund] = useState(null);
    const [showFundWithdraw, setShowFundWithdraw] = useState(false);
    const [fundWithdrawing, setFundWithdrawing] = useState(false);
    const [refreshHours, setRefreshHours] = useState(1);

    const [notification, setNotification] = useState({ type: '', text: '' });

    function loadBalances() {
        if (!fiat) return;
        setIsLoading(true);
        getFullBalance(fiat, isFuture)
            .then((info) => {
                if (!info || typeof info !== 'object') {
                    setBalances([]);
                    setTotalFiat(0);
                    setWalletKind('');
                    setAllocation(null);
                    setPnl(null);
                    setPnlPerc(null);
                    setPnlMode('');
                    setIsLoading(false);
                    return;
                }

                const skipWalletKeys = new Set([
                    'fiatEstimate', 'walletKind', 'allocation', 'allocationFiat',
                    'depositedUsdc', 'depositedFiat',
                    'pnl', 'pnlPerc', 'net', 'deployed', 'equity',
                    'pnlMode', 'dayOpenUsdc', 'dayOpenOn',
                    'walletRefreshHours', 'walletCachedAt', 'walletNextRefreshAt'
                ]);
                const nextBalances = Object.entries(info)
                    .filter(([key, value]) => !skipWalletKeys.has(key) && value && typeof value === 'object' && value.available !== undefined)
                    .map(([symbol, data]) => ({
                        symbol,
                        available: data.available,
                        onOrder: data.onOrder,
                        fiatEstimate: data.fiatEstimate,
                        avg: data.avg
                    }))
                    .filter(b => parseFloat(b.available) > 0 || parseFloat(b.onOrder) > 0 || parseFloat(b.available) < 0)
                    .sort((a, b) => {
                        if (a.symbol > b.symbol) return 1;
                        if (a.symbol < b.symbol) return -1;
                        return 0;
                    });

                const totalFromCoins = nextBalances.reduce((sum, item) => {
                    const estimate = parseFloat(item.fiatEstimate);
                    return sum + (Number.isFinite(estimate) ? estimate : 0);
                }, 0);
                const totalFromApi = typeof info.fiatEstimate === 'number'
                    ? info.fiatEstimate
                    : parseFloat(String(info.fiatEstimate || '').replace(/[^\d.-]/g, ''));

                setBalances(nextBalances);
                setTotalFiat(Number.isFinite(totalFromApi) ? totalFromApi : totalFromCoins);
                setWalletKind(info.walletKind || '');
                setPnlMode(info.pnlMode || '');
                setAllocation(info.pnlMode === 'daily'
                    ? null
                    : (Number.isFinite(Number(info.depositedFiat ?? info.depositedUsdc ?? info.allocationFiat ?? info.allocation))
                        ? Number(info.depositedFiat ?? info.depositedUsdc ?? info.allocationFiat ?? info.allocation)
                        : null));
                setPnl(Number.isFinite(Number(info.pnl)) ? Number(info.pnl) : null);
                setPnlPerc(Number.isFinite(Number(info.pnlPerc)) ? Number(info.pnlPerc) : null);
                if (Number(info.walletRefreshHours) > 0)
                    setRefreshHours(Number(info.walletRefreshHours));
                setIsLoading(false);
            })
            .catch(err => {
                console.error(apiError(err));
                setNotification({ type: 'error', text: apiError(err) });
                setIsLoading(false);
            });
    }

    useEffect(() => {
        loadBalances();
    }, [fiat, isFuture]);

    useEffect(() => {
        const ms = Math.max(1, Number(refreshHours) || 1) * 60 * 60 * 1000;
        const timer = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            loadBalances();
        }, ms);
        return () => clearInterval(timer);
    }, [fiat, isFuture, refreshHours]);

    function loadFund() {
        if (!isCustody) return;
        getClientFund()
            .then(result => setFund(result || null))
            .catch(() => setFund(null));
    }

    useEffect(() => {
        if (!isCustody) return;
        getSettings()
            .then(settings => setPixKey(settings?.pixKey || ''))
            .catch(() => setPixKey(''));
        loadFund();
    }, [isCustody]);

    function onOrderSubmit(order) {
        window.location.reload();
    }

    function onFiatChange(event) {
        setDefaultFiat(event.target.value);
        setFiat(event.target.value);
    }

    function openWithdraw() {
        setShowWithdraw(true);
    }

    async function confirmWithdraw() {
        if (!pixKey) {
            setNotification({ type: 'error', text: 'Não há chave PIX cadastrada. Peça ao administrador para incluir a chave no seu cadastro.' });
            setShowWithdraw(false);
            return;
        }
        setWithdrawing(true);
        try {
            const result = await doCustodyWithdraw();
            setShowWithdraw(false);
            setNotification({
                type: 'success',
                text: result?.message || 'Saldo será convertido para BRL e enviado para a chave PIX cadastrada.'
            });
            loadBalances();
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setWithdrawing(false);
        }
    }

    function openFundWithdraw() {
        setShowFundWithdraw(true);
    }

    async function confirmFundWithdraw() {
        setFundWithdrawing(true);
        try {
            const result = await requestFundWithdraw();
            setShowFundWithdraw(false);
            setNotification({
                type: 'success',
                text: result?.message || 'O saldo cai em até 24 horas na chave PIX cadastrada.'
            });
            loadFund();
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setFundWithdrawing(false);
        }
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="wallet-container">
                    <div className="wallet-header">
                        <h2>Carteira</h2>
                        <div className="wallet-toolbar">
                            {
                                localStorage.getItem("hasFutures") === "true"
                                    ? (
                                        <button className='wallet-btn-secondary' onClick={() => setIsFuture(!isFuture)}>
                                            {
                                                isFuture
                                                    ? "Futures"
                                                    : "Spot"
                                            }
                                        </button>
                                    )
                                    : <></>
                            }
                            <SelectFiat onChange={onFiatChange} fiat={fiat} />
                            {isCustody
                                ? (
                                    <Link to="/deposito" className="wallet-btn-deposit">
                                        Depositar
                                    </Link>
                                )
                                : <></>}
                            {isCustody
                                ? (
                                    <button
                                        type="button"
                                        className="wallet-btn-withdraw"
                                        onClick={openWithdraw}
                                        disabled={withdrawing || isLoading}
                                    >
                                        Sacar
                                    </button>
                                )
                                : <></>}
                            <NewOrderButton />
                        </div>
                    </div>

                    {!isLoading && (totalFiat > 0 || pnl != null) && (
                        <div className="wallet-summary-card">
                            <div>
                                <div className="wallet-summary-label">Total da Carteira</div>
                                <div className="wallet-summary-value">
                                    {formatFiatAmount(totalFiat, fiat)}
                                </div>
                                {(walletKind === 'allocated' || walletKind === 'custody')
                                    ? (
                                        <div className="wallet-summary-label" style={{ marginTop: 8 }}>
                                            USDC livre mais as moedas das posições, ao preço de agora. Lucro e prejuízo ficam nesta carteira.
                                        </div>
                                    )
                                    : (fiat === 'USD' || fiat === 'USDT' || fiat === 'USDC') && (
                                    <div className="wallet-summary-label" style={{ marginTop: 8 }}>
                                        Este total está em dólar. Na Binance o mesmo patrimônio aparece em reais. Escolha BRL no seletor para comparar.
                                    </div>
                                )}
                            </div>
                            {pnl != null && (
                                <div>
                                    <div className="wallet-summary-label">{pnlMode === 'daily' ? 'PnL hoje' : 'PnL'}</div>
                                    <div className={`wallet-summary-value wallet-summary-pnl ${pnl > 0 ? 'is-gain' : pnl < 0 ? 'is-loss' : ''}`}>
                                        {pnl > 0 ? '+' : ''}{formatFiatAmount(pnl, fiat)}
                                        {pnlPerc != null ? ` (${pnlPerc > 0 ? '+' : ''}${pnlPerc.toFixed(2)}%)` : ''}
                                    </div>
                                    {pnlMode === 'daily'
                                        ? (
                                            <div className="wallet-summary-label" style={{ marginTop: 8 }}>
                                                Variação da carteira Binance desde o começo do dia, com as moedas ao preço de agora.
                                            </div>
                                        )
                                        : (allocation != null && allocation > 0 && (
                                        <div className="wallet-summary-label" style={{ marginTop: 8 }}>
                                            Versus {formatFiatAmount(allocation, fiat)} depositado em carteira
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div>
                                <div className="wallet-summary-label">Ativos</div>
                                <div className="wallet-summary-value" style={{ fontSize: '1.5rem' }}>
                                    {balances.length}
                                </div>
                            </div>
                        </div>
                    )}

                    {isCustody && fund
                        ? (
                            <section className="wallet-fund-card">
                                <div className="wallet-fund-head">
                                    <div>
                                        <div className="wallet-summary-label">Taxas a receber</div>
                                        <div className="wallet-summary-value">
                                            {Number(fund.payable || 0).toLocaleString('pt-BR', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })} USDT
                                        </div>
                                        {Number(fund.usdcPending) > 0
                                            ? (
                                                <div className="wallet-summary-label" style={{ marginTop: 8 }}>
                                                    {Number(fund.usdcPending).toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })} USDC ainda em conversão
                                                </div>
                                            )
                                            : null}
                                    </div>
                                    <button
                                        type="button"
                                        className="wallet-btn-withdraw"
                                        onClick={openFundWithdraw}
                                        disabled={
                                            fundWithdrawing
                                            || !fund.withdraw?.open
                                            || !(Number(fund.payable) > 0)
                                            || Boolean(fund.pendingPayout)
                                            || !pixKey
                                        }
                                    >
                                        {fund.pendingPayout
                                            ? 'Saque pedido'
                                            : fundWithdrawing
                                                ? 'Solicitando...'
                                                : 'Sacar'}
                                    </button>
                                </div>
                                <div className="wallet-fund-progress">
                                    <div className="wallet-fund-progress-top">
                                        <span>Progresso até o dia 15</span>
                                        <strong>{Number(fund.withdraw?.percent) || 0}%</strong>
                                    </div>
                                    <div
                                        className="wallet-fund-bar"
                                        role="progressbar"
                                        aria-valuenow={Number(fund.withdraw?.percent) || 0}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        <span style={{ width: `${Math.max(0, Math.min(100, Number(fund.withdraw?.percent) || 0))}%` }} />
                                    </div>
                                    <p className="wallet-fund-hint">
                                        {fund.pendingPayout
                                            ? 'Saque solicitado. O crédito cai em até 24 horas na chave PIX cadastrada.'
                                            : fund.withdraw?.open
                                                ? 'Hoje é dia 15. Você já pode sacar as taxas a receber.'
                                                : `O saque abre em ${fund.withdraw?.nextOpenLabel || '15 do mês'}${fund.withdraw?.daysLeft
                                                    ? ` · faltam ${fund.withdraw.daysLeft} dia(s)`
                                                    : ''}.`}
                                    </p>
                                </div>
                            </section>
                        )
                        : null}

                    {isLoading ? (
                        <div className="loading-text">Carregando...</div>
                    ) : balances && balances.length ? (
                        <div className="wallet-cards-grid">
                            {balances.map(item => (
                                <WalletCard key={item.symbol} data={item} fiat={fiat} />
                            ))}
                        </div>
                    ) : (
                        <div className="loading-text">Nenhum saldo encontrado.</div>
                    )}
                </div>
                <Footer />
            </main>
            <NewOrderModal onSubmit={onOrderSubmit} />
            {showWithdraw && (
                <div className="wallet-withdraw-overlay" role="dialog" aria-modal="true">
                    <div className="wallet-withdraw-modal">
                        <h3>Confirmar saque</h3>
                        {pixKey
                            ? (
                                <>
                                    <p>Antes de sacar, leia com atenção o que vai acontecer:</p>
                                    <ul>
                                        <li>Todas as moedas da carteira serão vendidas por USDC.</li>
                                        <li>Se houver monitor rodando, a posição será vendida e o monitor será excluído.</li>
                                        <li>O saldo em USDC será atualizado e zerado para o saque.</li>
                                        <li>Esse saldo será convertido para BRL e enviado para a chave PIX cadastrada:</li>
                                    </ul>
                                    <p className="wallet-withdraw-pix"><strong>{pixKey}</strong></p>
                                    <p>Esta ação não pode ser desfeita. O crédito na conta pode levar algum tempo após o processamento.</p>
                                </>
                            )
                            : (
                                <p>Não há chave PIX cadastrada. Peça ao administrador para incluir a chave no seu cadastro antes de sacar.</p>
                            )}
                        <div className="wallet-withdraw-actions">
                            <button
                                type="button"
                                className="wallet-btn-secondary"
                                onClick={() => setShowWithdraw(false)}
                                disabled={withdrawing}
                            >
                                Cancelar
                            </button>
                            {pixKey
                                ? (
                                    <button
                                        type="button"
                                        className="wallet-btn-withdraw"
                                        onClick={confirmWithdraw}
                                        disabled={withdrawing}
                                    >
                                        {withdrawing ? 'Sacando...' : 'Confirmar saque'}
                                    </button>
                                )
                                : <></>}
                        </div>
                    </div>
                </div>
            )}
            {showFundWithdraw && (
                <div className="wallet-withdraw-overlay" role="dialog" aria-modal="true">
                    <div className="wallet-withdraw-modal">
                        <h3>Sacar taxas a receber</h3>
                        <p>
                            O saldo cai em até 24 horas na chave PIX cadastrada.
                        </p>
                        {pixKey
                            ? <p className="wallet-withdraw-pix"><strong>{pixKey}</strong></p>
                            : <p>Não há chave PIX cadastrada. Peça ao administrador para incluir a chave no seu cadastro.</p>}
                        <p>
                            Valor deste saque:{' '}
                            <strong>
                                {Number(fund?.payable || 0).toLocaleString('pt-BR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })} USDT
                            </strong>
                        </p>
                        <div className="wallet-withdraw-actions">
                            <button
                                type="button"
                                className="wallet-btn-secondary"
                                onClick={() => setShowFundWithdraw(false)}
                                disabled={fundWithdrawing}
                            >
                                Cancelar
                            </button>
                            {pixKey
                                ? (
                                    <button
                                        type="button"
                                        className="wallet-btn-withdraw"
                                        onClick={confirmFundWithdraw}
                                        disabled={fundWithdrawing}
                                    >
                                        {fundWithdrawing ? 'Solicitando...' : 'Confirmar saque'}
                                    </button>
                                )
                                : null}
                        </div>
                    </div>
                </div>
            )}
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default Wallet;
